/**
 * Wave Domain Mock Data and Functions
 */

import type { ApiResponse, PageData } from '../../types/common';
import type {
  WaveRuleDTO,
  QueryWaveRulePageRequest,
  CreateWaveRuleRequest,
  UpdateWaveRuleRequest,
} from '../../types/wave/waveRule';
import type {
  WaveInstanceDTO,
  WaveInstanceOrderDTO,
  QueryWaveInstancePageRequest,
  QueryWaveOrdersRequest,
  TriggerWaveRequest,
} from '../../types/wave/waveInstance';
import type {
  WaveConfigLogDTO,
  QueryWaveConfigLogPageRequest,
} from '../../types/wave/waveConfigLog';
import {
  WaveRuleStatus,
  LaunchCycle,
  TaskTriggerType,
  WaveTimeReferenceType,
  WaveStatus,
  WaveType,
} from '../../constants/wave';

/**
 * Mock Utility - Success Response
 */
function mockSuccess<T>(data: T): ApiResponse<T> {
  return {
    success: true,
    errorMessage: null,
    flag: 'success',
    data,
    traceId: `mock-${Date.now()}`,
  };
}

/**
 * Mock Utility - Fail Response
 */
function mockFail(msg: string): ApiResponse<null> {
  return {
    success: false,
    errorMessage: msg,
    flag: 'fail',
    data: null,
    traceId: `mock-${Date.now()}`,
  };
}

// ========== Wave Rule Mock Data ==========
const mockWaveRules: WaveRuleDTO[] = [
  {
    id: 1,
    code: 'WR001',
    ruleCode: 'WR001',
    ruleName: 'Go fresh template',
    effectiveStartDate: Date.now() - 86400000 * 30,
    effectiveEndDate: Date.now() + 86400000 * 90,
    description: 'Morning wave for daily orders',
    storeCode: 'S001',
    storeName: 'Lotus Store Bangkok Central',
    timeReferenceType: WaveTimeReferenceType.ORDER_CREATE_TIME,
    taskTriggerTypes: [TaskTriggerType.BY_ORDER_COUNT],
    triggerOrderCount: 10,
    triggerTimeMinutes: null,
    launchCycle: LaunchCycle.DAILY,
    dailyInterval: 1,
    weekdays: null,
    launchTimePeriods: [{ startTime: '08:00', endTime: '10:00' }],
    applyStores: ['STORE1', 'STORE2', 'STORE3'],
    status: WaveRuleStatus.ENABLED,
    executionMode: 'MANUAL_PICKING',
    creatorAccount: 'admin',
    creatorName: 'Admin User',
    gmtCreate: Date.now() - 86400000 * 30,
    modifierAccount: 'admin',
    modifierName: 'Admin User',
    gmtModified: Date.now() - 86400000,
  },
  {
    id: 2,
    code: 'WR002',
    ruleCode: 'WR002',
    ruleName: 'Supermarket template',
    effectiveStartDate: Date.now() - 86400000 * 15,
    effectiveEndDate: Date.now() + 86400000 * 60,
    description: 'Weekly return outbound wave',
    storeCode: 'S002',
    storeName: 'Lotus Store Chiang Mai',
    timeReferenceType: WaveTimeReferenceType.ORDER_SYNC_TIME,
    taskTriggerTypes: [TaskTriggerType.BY_TIME_INTERVAL],
    triggerOrderCount: null,
    triggerTimeMinutes: 60,
    launchCycle: LaunchCycle.WEEKLY,
    dailyInterval: null,
    weekdays: [1, 3, 5],
    launchTimePeriods: [{ startTime: '08:00', endTime: '10:00' }],
    applyStores: ['STORE1', 'STORE2', 'STORE3', 'STORE4', 'STORE5'],
    status: WaveRuleStatus.DISABLED,
    executionMode: 'BATCH_PICKING',
    creatorAccount: 'admin',
    creatorName: 'Admin User',
    gmtCreate: Date.now() - 86400000 * 15,
    modifierAccount: 'admin',
    modifierName: 'Admin User',
    gmtModified: Date.now() - 86400000 * 2,
  },
  {
    id: 3,
    code: 'WR003',
    ruleCode: 'WR003',
    ruleName: 'Hyper order picking template',
    effectiveStartDate: Date.now() - 86400000 * 10,
    effectiveEndDate: Date.now() + 86400000 * 120,
    description: 'Order picking for hyper stores',
    storeCode: 'S003',
    storeName: 'Lotus Store Phuket',
    timeReferenceType: WaveTimeReferenceType.ORDER_CREATE_TIME,
    taskTriggerTypes: [TaskTriggerType.BY_ORDER_COUNT],
    triggerOrderCount: 20,
    triggerTimeMinutes: null,
    launchCycle: LaunchCycle.DAILY,
    dailyInterval: 1,
    weekdays: null,
    launchTimePeriods: [{ startTime: '09:00', endTime: '11:00' }],
    applyStores: ['HP001', 'HP002', 'HP003'],
    status: WaveRuleStatus.ENABLED,
    executionMode: 'ORDER_PICKING',
    creatorAccount: 'admin',
    creatorName: 'Admin User',
    gmtCreate: Date.now() - 86400000 * 10,
    modifierAccount: 'admin',
    modifierName: 'Admin User',
    gmtModified: Date.now() - 86400000 * 3,
  },

];

export function mockPageWaveRules(
  params: QueryWaveRulePageRequest
): Promise<ApiResponse<PageData<WaveRuleDTO>>> {
  const { currentPage = 1, pageSize = 20, ruleCode, ruleName, status, applyStoreCode } = params;
  let data = [...mockWaveRules];
  
  if (ruleCode) {
    data = data.filter((r) => r.ruleCode.toLowerCase().includes(ruleCode.toLowerCase()));
  }
  if (ruleName) {
    data = data.filter((r) => r.ruleName.toLowerCase().includes(ruleName.toLowerCase()));
  }
  if (status) {
    data = data.filter((r) => r.status === status);
  }
  if (applyStoreCode) {
    data = data.filter((r) => (r.applyStores || []).includes(applyStoreCode));
  }
  
  const start = (currentPage - 1) * pageSize;
  const tableData = data.slice(start, start + pageSize);
  
  return Promise.resolve(
    mockSuccess({
      tableData,
      paging: { currentPage, pageSize, totalCount: data.length },
    })
  );
}

export function mockGetWaveRuleByCode(
  ruleCode: string
): Promise<ApiResponse<WaveRuleDTO>> {
  const rule = mockWaveRules.find((r) => r.ruleCode === ruleCode);
  return rule
    ? Promise.resolve(mockSuccess(rule))
    : Promise.resolve(mockFail('Wave rule not found') as any);
}

export function mockCreateWaveRule(
  data: CreateWaveRuleRequest
): Promise<ApiResponse<WaveRuleDTO>> {
  const newRule: WaveRuleDTO = {
    id: Date.now(),
    code: `WR${Date.now()}`,
    ruleCode: `WR${Date.now()}`,
    ruleName: data.ruleName,
    effectiveStartDate: data.effectiveStartDate,
    effectiveEndDate: data.effectiveEndDate || null,
    description: data.description || null,
    storeCode: data.storeCode,
    storeName: 'Mock Store',
    timeReferenceType: data.timeReferenceType,
    taskTriggerTypes: data.taskTriggerTypes,
    triggerOrderCount: data.triggerOrderCount || null,
    triggerTimeMinutes: data.triggerTimeMinutes || null,
    launchCycle: data.launchCycle,
    dailyInterval: data.dailyInterval || null,
    weekdays: data.weekdays || null,
    launchTimePeriods: data.launchTimePeriods,
    applyStores: data.applyStores || [],
    status: WaveRuleStatus.ENABLED,
    creatorAccount: 'admin',
    creatorName: 'Admin User',
    gmtCreate: Date.now(),
    modifierAccount: 'admin',
    modifierName: 'Admin User',
    gmtModified: Date.now(),
  };
  mockWaveRules.push(newRule);
  return Promise.resolve(mockSuccess(newRule));
}

export function mockUpdateWaveRule(
  data: UpdateWaveRuleRequest
): Promise<ApiResponse<WaveRuleDTO>> {
  const idx = mockWaveRules.findIndex((r) => r.ruleCode === data.ruleCode);
  if (idx === -1) {
    return Promise.resolve(mockFail('Rule not found') as any);
  }
  mockWaveRules[idx] = {
    ...mockWaveRules[idx],
    ...data,
    gmtModified: Date.now(),
  };
  return Promise.resolve(mockSuccess(mockWaveRules[idx]));
}

export function mockEnableWaveRule(
  ruleCode: string
): Promise<ApiResponse<WaveRuleDTO>> {
  const rule = mockWaveRules.find((r) => r.ruleCode === ruleCode);
  if (!rule) {
    return Promise.resolve(mockFail('Rule not found') as any);
  }
  rule.status = WaveRuleStatus.ENABLED;
  rule.gmtModified = Date.now();
  return Promise.resolve(mockSuccess(rule));
}

export function mockDisableWaveRule(
  ruleCode: string
): Promise<ApiResponse<WaveRuleDTO>> {
  const rule = mockWaveRules.find((r) => r.ruleCode === ruleCode);
  if (!rule) {
    return Promise.resolve(mockFail('Rule not found') as any);
  }
  rule.status = WaveRuleStatus.DISABLED;
  rule.gmtModified = Date.now();
  return Promise.resolve(mockSuccess(rule));
}

export function mockDeleteWaveRules(
  ruleCodes: string[]
): Promise<ApiResponse<null>> {
  ruleCodes.forEach((code) => {
    const idx = mockWaveRules.findIndex((r) => r.ruleCode === code);
    if (idx !== -1) {
      mockWaveRules.splice(idx, 1);
    }
  });
  return Promise.resolve(mockSuccess(null));
}

// ========== Wave Instance Mock Data ==========
const mockWaveInstances: WaveInstanceDTO[] = [
  {
    id: 1,
    code: 'WV20260302001',
    waveNo: 'WV20260302001',
    waveAttribute: null,
    waveStatus: WaveStatus.GENERATE_COMPLETE,
    successOrderCount: 10,
    failedOrderCount: 0,
    generatedTaskCount: 3,
    wavePlanCode: 'WR001',
    wavePlanName: 'Daily Morning Wave Rule',
    waveType: WaveType.AUTO,
    scheduleTime: Date.now() - 7200000,
    actualTriggerTime: Date.now() - 7100000,
    storeCode: 'S001',
    storeName: 'Lotus Store Bangkok Central',
    creatorAccount: 'system',
    creatorName: 'System',
    gmtCreate: Date.now() - 7200000,
    modifierAccount: 'system',
    modifierName: 'System',
    gmtModified: Date.now() - 3600000,
  },
  {
    id: 2,
    code: 'WV20260302002',
    waveNo: 'WV20260302002',
    waveAttribute: null,
    waveStatus: WaveStatus.GENERATE_COMPLETE,
    successOrderCount: 5,
    failedOrderCount: 0,
    generatedTaskCount: 2,
    wavePlanCode: 'WR001',
    wavePlanName: 'Daily Morning Wave Rule',
    waveType: WaveType.AUTO,
    scheduleTime: Date.now() - 3600000,
    actualTriggerTime: Date.now() - 3500000,
    storeCode: 'S001',
    storeName: 'Lotus Store Bangkok Central',
    creatorAccount: 'system',
    creatorName: 'System',
    gmtCreate: Date.now() - 3600000,
    modifierAccount: 'system',
    modifierName: 'System',
    gmtModified: Date.now() - 1800000,
  },
  {
    id: 3,
    code: 'WV20260302003',
    waveNo: 'WV20260302003',
    waveAttribute: null,
    waveStatus: WaveStatus.GENERATE_COMPLETE,
    successOrderCount: 15,
    failedOrderCount: 0,
    generatedTaskCount: 5,
    wavePlanCode: 'WR002',
    wavePlanName: 'Weekly Return Wave Rule',
    waveType: WaveType.AUTO,
    scheduleTime: Date.now() - 5400000,
    actualTriggerTime: Date.now() - 5300000,
    storeCode: 'S002',
    storeName: 'Lotus Store Chiang Mai',
    creatorAccount: 'system',
    creatorName: 'System',
    gmtCreate: Date.now() - 5400000,
    modifierAccount: 'system',
    modifierName: 'System',
    gmtModified: Date.now() - 3600000,
  },
];

const mockWaveOrders: WaveInstanceOrderDTO[] = [
  // Orders for WV20260302001
  {
    waveNo: 'WV20260302001',
    orderNo: 'ORD20260301001',
    orderStatus: 'CREATED',
    allocationStatus: 'SUCCESS',
    failReason: null,
    taskCode: null,
  },
  {
    waveNo: 'WV20260302001',
    orderNo: 'ORD20260301002',
    orderStatus: 'IN_PROGRESS',
    allocationStatus: 'SUCCESS',
    failReason: null,
    taskCode: 'TASK-20260301-001',
  },
  {
    waveNo: 'WV20260302001',
    orderNo: 'ORD20260301003',
    orderStatus: 'COMPLETED',
    allocationStatus: 'SUCCESS',
    failReason: null,
    taskCode: 'TASK-20260301-001',
  },
  // Orders for WV20260302002
  {
    waveNo: 'WV20260302002',
    orderNo: 'ORD20260302001',
    orderStatus: 'IN_PROGRESS',
    allocationStatus: 'SUCCESS',
    failReason: null,
    taskCode: 'TASK-20260302-001',
  },
  {
    waveNo: 'WV20260302002',
    orderNo: 'ORD20260302002',
    orderStatus: 'CREATED',
    allocationStatus: 'SUCCESS',
    failReason: null,
    taskCode: null,
  },
  {
    waveNo: 'WV20260302002',
    orderNo: 'ORD20260302003',
    orderStatus: 'CREATED',
    allocationStatus: 'FAILED',
    failReason: 'Insufficient inventory',
    taskCode: null,
  },
  {
    waveNo: 'WV20260302002',
    orderNo: 'ORD20260302004',
    orderStatus: 'CREATED',
    allocationStatus: 'FAILED',
    failReason: 'Location not found',
    taskCode: null,
  },
  {
    waveNo: 'WV20260302002',
    orderNo: 'ORD20260302005',
    orderStatus: 'CREATED',
    allocationStatus: 'FAILED',
    failReason: 'SKU not available',
    taskCode: null,
  },
  // Orders for WV20260302003
  {
    waveNo: 'WV20260302003',
    orderNo: 'ORD20260303001',
    orderStatus: 'COMPLETED',
    allocationStatus: 'SUCCESS',
    failReason: null,
    taskCode: 'TASK-20260303-001',
  },
  {
    waveNo: 'WV20260302003',
    orderNo: 'ORD20260303002',
    orderStatus: 'COMPLETED',
    allocationStatus: 'SUCCESS',
    failReason: null,
    taskCode: 'TASK-20260303-001',
  },
  {
    waveNo: 'WV20260302003',
    orderNo: 'ORD20260303003',
    orderStatus: 'IN_PROGRESS',
    allocationStatus: 'SUCCESS',
    failReason: null,
    taskCode: 'TASK-20260303-002',
  },
  {
    waveNo: 'WV20260302003',
    orderNo: 'ORD20260303004',
    orderStatus: 'CREATED',
    allocationStatus: 'SUCCESS',
    failReason: null,
    taskCode: null,
  },
  {
    waveNo: 'WV20260302003',
    orderNo: 'ORD20260303005',
    orderStatus: 'CANCELLED',
    allocationStatus: 'SUCCESS',
    failReason: null,
    taskCode: 'TASK-20260303-002',
  },
];

export function mockPageWaveInstances(
  params: QueryWaveInstancePageRequest
): Promise<ApiResponse<PageData<WaveInstanceDTO>>> {
  const { currentPage = 1, pageSize = 20, waveNo, waveStatus, waveType } = params;
  let data = [...mockWaveInstances];
  
  if (waveNo) {
    data = data.filter((w) => w.waveNo.toLowerCase().includes(waveNo.toLowerCase()));
  }
  if (waveStatus) {
    data = data.filter((w) => w.waveStatus === waveStatus);
  }
  if (waveType) {
    data = data.filter((w) => w.waveType === waveType);
  }
  
  const start = (currentPage - 1) * pageSize;
  const tableData = data.slice(start, start + pageSize);
  
  return Promise.resolve(
    mockSuccess({
      tableData,
      paging: { currentPage, pageSize, totalCount: data.length },
    })
  );
}

export function mockGetWaveInstanceByNo(
  waveNo: string
): Promise<ApiResponse<WaveInstanceDTO>> {
  const instance = mockWaveInstances.find((w) => w.waveNo === waveNo);
  return instance
    ? Promise.resolve(mockSuccess(instance))
    : Promise.resolve(mockFail('Wave instance not found') as any);
}

export function mockGetWaveOrders(
  waveNo: string,
  params: QueryWaveOrdersRequest
): Promise<ApiResponse<PageData<WaveInstanceOrderDTO>>> {
  const { currentPage = 1, pageSize = 20, allocationStatus } = params;
  let data = mockWaveOrders.filter((o) => o.waveNo === waveNo);
  
  if (allocationStatus) {
    data = data.filter((o) => o.allocationStatus === allocationStatus);
  }
  
  const start = (currentPage - 1) * pageSize;
  const tableData = data.slice(start, start + pageSize);
  
  return Promise.resolve(
    mockSuccess({
      tableData,
      paging: { currentPage, pageSize, totalCount: data.length },
    })
  );
}

export function mockTriggerWave(
  data: TriggerWaveRequest
): Promise<ApiResponse<WaveInstanceDTO>> {
  const newInstance: WaveInstanceDTO = {
    id: Date.now(),
    code: `WV${Date.now()}`,
    waveNo: `WV${Date.now()}`,
    waveAttribute: null,
    waveStatus: WaveStatus.GENERATE_COMPLETE,
    successOrderCount: data.orderNos?.length || 5,
    failedOrderCount: 0,
    generatedTaskCount: 1,
    wavePlanCode: data.ruleCode,
    wavePlanName: 'Manually Triggered Wave',
    waveType: WaveType.MANUAL,
    scheduleTime: Date.now(),
    actualTriggerTime: Date.now(),
    storeCode: 'S001',
    storeName: 'Lotus Store Bangkok Central',
    creatorAccount: 'admin',
    creatorName: 'Admin User',
    gmtCreate: Date.now(),
    modifierAccount: 'admin',
    modifierName: 'Admin User',
    gmtModified: Date.now(),
  };
  mockWaveInstances.push(newInstance);
  return Promise.resolve(mockSuccess(newInstance));
}

// ========== Wave Config Log Mock Data ==========
const mockWaveConfigLogs: WaveConfigLogDTO[] = [
  {
    id: 1,
    storeCode: 'S001',
    storeName: 'Lotus Store Bangkok Central',
    ruleCode: 'WR001',
    ruleName: 'Go fresh template',
    status: WaveRuleStatus.ENABLED,
    gmtCreate: Date.now() - 86400000 * 30,
    gmtModified: Date.now() - 86400000,
    executionMode: 'MANUAL_PICKING',
    effectiveStartDate: Date.now() - 86400000 * 30,
    effectiveEndDate: Date.now() + 86400000 * 90,
  },
  {
    id: 2,
    storeCode: 'S002',
    storeName: 'Lotus Store Chiang Mai',
    ruleCode: 'WR001',
    ruleName: 'Go fresh template',
    status: WaveRuleStatus.ENABLED,
    gmtCreate: Date.now() - 86400000 * 25,
    gmtModified: Date.now() - 86400000 * 2,
    executionMode: 'MANUAL_PICKING',
    effectiveStartDate: Date.now() - 86400000 * 25,
    effectiveEndDate: Date.now() + 86400000 * 60,
  },
  {
    id: 3,
    storeCode: 'S003',
    storeName: 'Lotus Store Phuket',
    ruleCode: 'WR002',
    ruleName: 'Supermarket template',
    status: WaveRuleStatus.DISABLED,
    gmtCreate: Date.now() - 86400000 * 15,
    gmtModified: Date.now() - 86400000 * 5,
    executionMode: 'BATCH_PICKING',
    effectiveStartDate: Date.now() - 86400000 * 15,
    effectiveEndDate: Date.now() + 86400000 * 45,
  },
  {
    id: 4,
    storeCode: 'S004',
    storeName: 'Lotus Store Pattaya',
    ruleCode: 'WR002',
    ruleName: 'Supermarket template',
    status: WaveRuleStatus.ENABLED,
    gmtCreate: Date.now() - 86400000 * 10,
    gmtModified: Date.now() - 86400000 * 3,
    executionMode: 'BATCH_PICKING',
    effectiveStartDate: Date.now() - 86400000 * 10,
    effectiveEndDate: null,
  },
  {
    id: 5,
    storeCode: 'S005',
    storeName: 'Lotus Store Korat',
    ruleCode: 'WR001',
    ruleName: 'Go fresh template',
    status: WaveRuleStatus.ENABLED,
    gmtCreate: Date.now() - 86400000 * 5,
    gmtModified: Date.now() - 86400000,
    executionMode: 'MANUAL_PICKING',
    effectiveStartDate: Date.now() - 86400000 * 5,
    effectiveEndDate: Date.now() + 86400000 * 120,
  },
];

export function mockPageWaveConfigLogs(
  params: QueryWaveConfigLogPageRequest
): Promise<ApiResponse<PageData<WaveConfigLogDTO>>> {
  const { currentPage = 1, pageSize = 20, ruleCode, status, gmtCreateStart, gmtCreateEnd } = params;
  let data = [...mockWaveConfigLogs];

  if (ruleCode) {
    data = data.filter((item) => item.ruleCode.toLowerCase().includes(ruleCode.toLowerCase()));
  }
  if (status) {
    data = data.filter((item) => item.status === status);
  }
  if (gmtCreateStart) {
    data = data.filter((item) => item.gmtCreate >= gmtCreateStart);
  }
  if (gmtCreateEnd) {
    data = data.filter((item) => item.gmtCreate <= gmtCreateEnd);
  }

  const start = (currentPage - 1) * pageSize;
  const tableData = data.slice(start, start + pageSize);

  return Promise.resolve(
    mockSuccess({
      tableData,
      paging: { currentPage, pageSize, totalCount: data.length },
    })
  );
}
